import React, { useState } from 'react';
import axios from 'axios';
import './taxwithheldcalculator.css';
import { API_BASE_URL, NGROK_URL } from '../../utils/const';

const TaxWithHeldCalculator = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [taxResult, setTaxResult] = useState(null);
  const [formData, setFormData] = useState({
    payeeName: '',
    paymentFrequency: 'weekly',
    medicareExemption: 'no',
    medicareReduction: 'yes',
    hasSpouse: 'yes',
    combinedIncomeLessThanThreshold: 'yes',
    dependentChildren: 0,
    taxFileNumberProvided: 'yes',
    payeeExemptedFromTFN: 'no',
    studyLoanDebt: 'no',
    taxStatus: 'non-resident',
    taxFreeThreshold: 'no',
    workingHolidayMaker: 'no'
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setTaxResult(null);

    try {
      const payload = {
        payee_name: formData.payeeName || null,
        frequency: formData.paymentFrequency,
        gross_earnings: Number(formData.grossWeeklyEarnings),
        tfn_provided: formData.taxFileNumberProvided,
        tfn_reason: formData.taxFileNumberProvided === 'no' ? formData.payeeExemptedFromTFN : null,
        payee_status: formData.taxStatus === 'working-holiday-maker' ? 'Working Holiday Maker' : 
                     formData.taxStatus === 'resident' ? 'Resident' : 
                     formData.taxStatus === 'non-resident' ? 'Non-Resident' : null,
        tax_free_threshold: formData.taxStatus === 'resident' ? formData.taxFreeThreshold : null,
        registered_employer: formData.taxStatus === 'working-holiday-maker' ? formData.workingHolidayMaker : null,
        medicare_levy_exemption: formData.medicareExemption,
        medicare_levy_reduction: formData.medicareReduction,
        payee_spouse: formData.hasSpouse,
        combined_income_table_a: formData.hasSpouse === 'yes' ? formData.combinedIncomeLessThanThreshold : null,
        dependent_children: Number(formData.dependentChildren),
        tax_offsets: 0 // Default value as per sample payloads
      };

      const response = await axios.post(
        API_BASE_URL+'/ato_calculator/calculate-tax_withheld_for_individuals_calculator',
        payload
      );

      setTaxResult(response.data.tax_withheld_amount);
      console.log('API Response:', response.data);

    } catch (error) {
      console.error('Error calculating tax withheld:', error);
      // You might want to show an error message to the user
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="form-container py-5">
      <form className="p-4 border rounded bg-white shadow-sm" onSubmit={handleSubmit}>
        <h1 className="mb-3 text-primary">Tax withheld for individuals calculator</h1>
        <p className="note">All fields marked with * are mandatory</p>

        {/* Payee's Name */}
        <div className="input-group">
          <label className="form-label">Payee's Name (optional) </label>
          <input
            type="text"
            className="form-control"
            name="payeeName"
            value={formData.payeeName}
            onChange={handleChange}
            placeholder="Enter payee's name"
          />
        </div>

        {/* Payment Frequency */}
        <div className="form-group">
          <label className="form-label">Payee receives payments</label>
          <div className="residency-options">
            {['weekly', 'fortnightly', 'monthly', 'quarterly'].map((frequency) => (
              <div
                key={frequency}
                className={`residency-option ${
                  formData.paymentFrequency === frequency ? 'selected' : ''
                }`}
              >
                <input
                  type="radio"
                  className="form-check-input"
                  id={frequency}
                  name="paymentFrequency"
                  value={frequency}
                  checked={formData.paymentFrequency === frequency}
                  onChange={handleChange}
                />
                <label className="form-check-label" htmlFor={frequency}>
                  {frequency.charAt(0).toUpperCase() + frequency.slice(1)}
                </label>
              </div>
            ))}
          </div>
        </div>
        <div className="form-group">
          <label className="form-label">Payee's gross {formData.paymentFrequency} earnings *</label>
          <div className="input-group">
            <span className="input-group-prepend">$</span>
            <input
              type="number"
              className="form-control"
              name="grossWeeklyEarnings"
              value={formData.grossWeeklyEarnings || ''}
              onChange={handleChange}
              placeholder="0"
            />
            <span className="input-group-append">.00</span>
          </div>
        </div>

        {/* Tax File Number Declaration */}
        <div className="form-group">
          <h4 className="form-label">Tax file number declaration</h4>
          <label>Has the payee provided a Tax File Number (TFN)? *</label>
          <div className="residency-options">
            {['yes', 'no'].map((option) => (
              <div
                key={option}
                className={`residency-option ${
                  formData.taxFileNumberProvided === option ? 'selected' : ''
                }`}
              >
                <input
                  type="radio"
                  className="form-check-input"
                  id={`taxFileNumberProvided-${option}`}
                  name="taxFileNumberProvided"
                  value={option}
                  checked={formData.taxFileNumberProvided === option}
                  onChange={handleChange}
                  required
                />
                <label
                  className="form-check-label"
                  htmlFor={`taxFileNumberProvided-${option}`}
                >
                  {option.charAt(0).toUpperCase() + option.slice(1)}
                </label>
              </div>
            ))}
          </div>
        </div>

        {/* Conditionally render the exemption radio buttons */}
        {formData.taxFileNumberProvided === 'no' && (
          <div className="form-group">
            <label>Is the payee exempt from needing a TFN? *</label>
            <div className="residency-options">
              {['yes', 'no'].map((option) => (
                <div
                  key={option}
                  className={`residency-option ${
                    formData.payeeExemptedFromTFN === option ? 'selected' : ''
                  }`}
                >
                  <input
                    type="radio"
                    className="form-check-input"
                    id={`payeeExemptedFromTFN-${option}`}
                    name="payeeExemptedFromTFN"
                    value={option}
                    checked={formData.payeeExemptedFromTFN === option}
                    onChange={handleChange}
                    required
                  />
                  <label
                    className="form-check-label"
                    htmlFor={`payeeExemptedFromTFN-${option}`}
                  >
                    {option.charAt(0).toUpperCase() + option.slice(1)}
                  </label>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="form-group">
          <label>What is the tax status of the payee? *</label>
          <div className="residency-options">
            {['resident', 'non-resident', 'working-holiday-maker'].map((option) => (
              <div
                key={option}
                className={`residency-option ${
                  formData.taxStatus === option ? 'selected' : ''
                }`}
              >
                <input
                  type="radio"
                  className="form-check-input"
                  id={`taxStatus-${option}`}
                  name="taxStatus"
                  value={option}
                  checked={formData.taxStatus === option}
                  onChange={handleChange}
                  required
                />
                <label
                  className="form-check-label"
                  htmlFor={`taxStatus-${option}`}
                >
                  {option.charAt(0).toUpperCase() + option.slice(1)}
                </label>
              </div>
            ))}
          </div>
        </div>

        {/* Conditionally render the exemption radio buttons */}
        {formData?.taxStatus === 'resident' && (
          <div className="form-group">
            <label>Has the payee claimed the Tax Free Threshold? *</label>
            <div className="residency-options">
              {['yes', 'no'].map((option) => (
                <div
                  key={option}
                  className={`residency-option ${
                    formData.taxFreeThreshold === option ? 'selected' : ''
                  }`}
                >
                  <input
                    type="radio"
                    className="form-check-input"
                    id={`taxFreeThreshold-${option}`}
                    name="taxFreeThreshold"
                    value={option}
                    checked={formData.taxFreeThreshold === option}
                    onChange={handleChange}
                    required
                  />
                  <label
                    className="form-check-label"
                    htmlFor={`taxFreeThreshold-${option}`}
                  >
                    {option.charAt(0).toUpperCase() + option.slice(1)}
                  </label>
                </div>
              ))}
            </div>
          </div>
        )}

        {formData?.taxStatus === 'working-holiday-maker' && (
          <div className="form-group">
            <label>Are you registered as an employer of Working Holiday Makers? *</label>
            <div className="residency-options">
              {['yes', 'no'].map((option) => (
                <div
                  key={option}
                  className={`residency-option ${
                    formData.workingHolidayMaker === option ? 'selected' : ''
                  }`}
                >
                  <input
                    type="radio"
                    className="form-check-input"
                    id={`workingHolidayMaker-${option}`}
                    name="workingHolidayMaker"
                    value={option}
                    checked={formData.workingHolidayMaker === option}
                    onChange={handleChange}
                    required
                  />
                  <label
                    className="form-check-label"
                    htmlFor={`workingHolidayMaker-${option}`}
                  >
                    {option.charAt(0).toUpperCase() + option.slice(1)}
                  </label>
                </div>
              ))}
            </div>
          </div>
        )}
        {/* Study Loan Debt */}
        <div className="form-group">
          <label>Does the payee have a Study and Training Support Loan debt? *</label>
          <div className="residency-options">
            {['yes', 'no'].map((option) => (
              <div
                key={option}
                className={`residency-option ${
                  formData.studyLoanDebt === option ? 'selected' : ''
                }`}
              >
                <input
                  type="radio"
                  className="form-check-input"
                  id={`studyLoanDebt-${option}`}
                  name="studyLoanDebt"
                  value={option}
                  checked={formData.studyLoanDebt === option}
                  onChange={handleChange}
                />
                <label className="form-check-label" htmlFor={`studyLoanDebt-${option}`}>
                  {option.charAt(0).toUpperCase() + option.slice(1)}
                </label>
              </div>
            ))}
          </div>
        </div>

        {/* Medicare Levy Section */}
        <div className="form-group">
          <label className="form-label">Medicare levy</label>
          <div className="form-group">
            <label>Is the payee claiming a Medicare levy exemption?</label>
            <div className="residency-options">
              {['no', 'half', 'full'].map((option) => (
                <div
                  key={option}
                  className={`residency-option ${
                    formData.medicareExemption === option ? 'selected' : ''
                  }`}
                >
                  <input
                    type="radio"
                    className="form-check-input"
                    id={`medicareExemption-${option}`}
                    name="medicareExemption"
                    value={option}
                    checked={formData.medicareExemption === option}
                    onChange={handleChange}
                  />
                  <label className="form-check-label" htmlFor={`medicareExemption-${option}`}>
                    {option.charAt(0).toUpperCase() + option.slice(1)}
                  </label>
                </div>
              ))}
            </div>
          </div>

          <div className="form-group">
            <label>Is the payee claiming a Medicare levy reduction? *</label>
            <div className="residency-options">
              {['yes', 'no'].map((option) => (
                <div
                  key={option}
                  className={`residency-option ${
                    formData.medicareReduction === option ? 'selected' : ''
                  }`}
                >
                  <input
                    type="radio"
                    className="form-check-input"
                    id={`medicareReduction-${option}`}
                    name="medicareReduction"
                    value={option}
                    checked={formData.medicareReduction === option}
                    onChange={handleChange}
                  />
                  <label className="form-check-label" htmlFor={`medicareReduction-${option}`}>
                    {option.charAt(0).toUpperCase() + option.slice(1)}
                  </label>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Spouse Section */}
        <div className="form-group">
          <label>Does the payee have a spouse? *</label>
          <div className="residency-options">
            {['yes', 'no'].map((option) => (
              <div
                key={option}
                className={`residency-option ${
                  formData.hasSpouse === option ? 'selected' : ''
                }`}
              >
                <input
                  type="radio"
                  className="form-check-input"
                  id={`hasSpouse-${option}`}
                  name="hasSpouse"
                  value={option}
                  checked={formData.hasSpouse === option}
                  onChange={handleChange}
                />
                <label className="form-check-label" htmlFor={`hasSpouse-${option}`}>
                  {option.charAt(0).toUpperCase() + option.slice(1)}
                </label>
              </div>
            ))}
          </div>
        </div>

        {/* Dependent Children */}
        <div className="input-group">
          <div className="form-group">
            <label>Number of dependent children claimed?</label>
            <input
              type="number"
              className="form-control"
              name="dependentChildren"
              value={formData.dependentChildren}
              onChange={handleChange}
              placeholder="0"
            />
          </div>
        </div>

        {/* Submit Button */}
        <button type="submit" className="btn btn-primary" disabled={isLoading}>
          {isLoading ? 'Calculating...' : 'Calculate Tax'}
        </button>
      </form>

      {/* Results Section */}
      <div className="results-section mt-4">
        {isLoading ? (
          <div className="loader"></div>
        ) : taxResult && (
          <div className="tax-results p-3 border rounded">
            <h3>Tax Calculation Results</h3>
            <p className="mb-2">
              <strong>Tax to be withheld:</strong> {taxResult}
            </p>
            {taxResult.message && (
              <p className="text-info">{taxResult.message}</p>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default TaxWithHeldCalculator;
